import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllPosts, performBlogAction } from '../../services/blogService.ts';
import type { BlogListItem } from '../../types/blog.ts';

export default function BlogAdminScreen() {
  const [posts, setPosts] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    console.log('[BlogAdminScreen] Component mounted');
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      console.log('[BlogAdminScreen] Loading posts...');
      setLoading(true);
      setError(null);
      const data = await getAllPosts();
      setPosts(data);
      console.log('[BlogAdminScreen] Loaded', data.length, 'posts');
    } catch (err) {
      console.error('[BlogAdminScreen] Error loading posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: 'publish' | 'archive' | 'delete', slug: string) => {
    if (action === 'delete') {
      const confirmed = window.confirm('Are you sure you want to delete this post? This action cannot be undone.');
      if (!confirmed) return;
    }

    try {
      console.log('[BlogAdminScreen] Performing action:', action, 'on post:', slug);
      setActionLoading(slug);
      await performBlogAction({ type: action, slug });
      console.log('[BlogAdminScreen] Action completed, reloading posts');
      await loadPosts();
    } catch (err) {
      console.error('[BlogAdminScreen] Action failed:', err);
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'archived':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Blog Administration</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage blog posts, publish drafts, and monitor content status.
                </p>
              </div>
              <div className="flex space-x-3">
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back to Site
                </Link>
                <button
                  onClick={loadPosts}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Posts Table */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Title</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Author</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Updated</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Published</th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {posts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                            No blog posts found. Posts will appear here when submitted via the blog ingest API.
                          </td>
                        </tr>
                      ) : (
                        posts.map((post) => (
                          <tr key={post.slug}>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <div>
                                <div className="font-medium text-gray-900">{post.title}</div>
                                <div className="text-gray-500 truncate max-w-xs">{post.excerpt}</div>
                                <div className="text-xs text-gray-400">/{post.slug}</div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <span className={getStatusBadge(post.status)}>
                                {post.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {post.author}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDate(post.updated_at)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDate(post.published_at)}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                              <div className="flex justify-end space-x-2">
                                <Link
                                  to={`/admin/blog/${post.slug}`}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  View
                                </Link>
                                {post.status === 'draft' && (
                                  <button
                                    onClick={() => handleAction('publish', post.slug)}
                                    disabled={actionLoading === post.slug}
                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  >
                                    {actionLoading === post.slug ? 'Publishing...' : 'Publish'}
                                  </button>
                                )}
                                {post.status === 'published' && (
                                  <button
                                    onClick={() => handleAction('archive', post.slug)}
                                    disabled={actionLoading === post.slug}
                                    className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                                  >
                                    {actionLoading === post.slug ? 'Archiving...' : 'Archive'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleAction('delete', post.slug)}
                                  disabled={actionLoading === post.slug}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                >
                                  {actionLoading === post.slug ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 